/**
 * SKIN-241 — the feedback categories a reviewer may pick, as the API serves them.
 *
 * **Per-study configuration, never a list in this repo.** The categories, their section
 * headings and which shot each applies to are modelled server-side as a real `Survey` of
 * type `TYPE_INTERNAL_QC` — sections are `SurveySection`s, categories are `SurveyField`s —
 * and are fetched per image field:
 *
 *     GET /analytics/:uuidInvestigation/clinical/qc-feedback-categories?imageFieldName=…
 *
 * The response is the universal section plus the one matching that image's shot type. A
 * category is referenced by its `SurveyField` id when picked, so hardcoding labels here
 * would produce a list that silently disagrees with the ids being written.
 *
 * That is also why nothing in this module is translated: the labels are study content
 * edited through the existing form editor, not UI chrome.
 */

/** One selectable category. `id` is the `SurveyField` id the API expects back. */
export interface QcCategory {
    id: number;
    name: string;
    label: string;
    /** `image`, `lesion` or `visit` (SKIN-577, a reason for flagging the visit) — which anchor it attaches to. */
    level: string;
    order: number;
}

/** A heading and the categories under it, in the order the API returned them. */
export interface QcCategorySection {
    /** The server-side section name, e.g. `ALL SHOTS`, `OVERVIEW ONLY`, `LESION`. */
    name: string;
    fields: QcCategory[];
}

/**
 * Where one set of categories is in its fetch.
 *
 * A union rather than `sections` + `loading` + `error` side by side, for the reason the
 * `ImageBytes` union in this directory already states: the contradictory combinations —
 * loading with sections, failed with sections, loading and failed at once — stop being
 * constructible instead of being avoided by convention.
 *
 * It is resolved **per eCRF image field**, which is what the endpoint is keyed on. Two
 * visit-wide scalars could not express that: one field's failed fetch painted a warning on
 * every card in the lesion, including the images whose categories had already arrived.
 */
export type QcCategoriesState =
    | { status: 'loading' }
    | { status: 'ready'; sections: QcCategorySection[] }
    | { status: 'failed'; error: string };

/** The sections when they have arrived, and an empty list otherwise. */
export function sectionsOf(state: QcCategoriesState): QcCategorySection[] {
    return state.status === 'ready' ? state.sections : [];
}
